<template>
  <tr :qaid="`apple-label-item.row-${itemIndex}`">
    <td v-for="column in columns" :key="column.key" class="b2b-grid__cell lg:table-cell">
      <div class="b2b-grid__label">
        {{ column.label }}
      </div>

      <div class="b2b-grid__content">
        <!-- If it's the SKU cell -->
        <div v-if="column.key === 'sku'" class="h-input flex items-center">
          {{ item[column.key] }}
        </div>

        <!-- If it's label/active toggle -->
        <div v-else-if="column.key === 'retailLabelStatus'" class="h-input flex items-center">
          <toggle-switch
            :id="`${item.id}-active-toggle`"
            :name="`${item.id}-active-toggle`"
            class="text-white"
            :value="item.retailLabelStatus"
            :sync="true"
            :disabled="disableToggles"
            validate="required"
            qaid="apple-label-item.toggle"
            @change="toggleItem"
          />
        </div>

        <div v-else-if="column.key === 'retailLabelPrice'">
          <form-group
            :id="`${item.sku}-${column.key}`"
            v-model="item[column.key]"
            v-validate="validatorValue(column.validate)"
            class="mb-0"
            :type="item.retailLabelStatus ? 'number' : 'text'"
            step="0.01"
            :data-vv-as="`${column.label}`"
            :disabled="inputsDisabled"
            :error="errors.first(`${item.sku}${column.key}`)"
            :label="column.label"
            :maxlength="maxLength(column.key)"
            :name="item.sku + column.key"
            :qaid="`apple-label-item.input-${column.key}`"
            :show-error-messages="true"
            :show-label="false"
            :warning="priceWarning(column.key)"
            @change="dirtyCheck"
            @blur="dirtyCheck"
          />
        </div>

        <div v-else-if="column.key === 'advertisedPrice' && !consumerFriendlyMode">
          <grid-advertised-price
            :qaid="`apple-label-item-${column.key}`"
            :price="item[column.key]"
          ></grid-advertised-price>
        </div>

        <!-- If it's any of the rest -->
        <div v-else>
          <form-group
            v-if="column.key != 'advertisedPrice'"
            :id="`${item.sku}-${column.key}`"
            v-model="item[column.key]"
            v-validate="setValidation(column.validate)"
            class="mb-0"
            type="text"
            :data-vv-as="`${column.label}`"
            :disabled="inputsDisabled"
            :error="errors.first(`${item.sku}${column.key}`)"
            :label="column.label"
            :maxlength="maxLength(column.key)"
            :name="item.sku + column.key"
            :qaid="`apple-label-item.input-${column.key}`"
            :show-error-messages="true"
            :show-label="false"
            :value="item[column.key]"
          />
        </div>
      </div>
    </td>
  </tr>
</template>

<script>
import {currencyToNumber} from '../../utils/util';
import ToggleSwitch from '@/components/ToggleSwitch';
import FormGroup from '@/components/FormGroup';
import GridAdvertisedPrice from '@/components/grid/GridAdvertisedPrice';
import {mapState} from 'vuex';

export default {
  inject: ['$validator'],

  components: {ToggleSwitch, FormGroup, GridAdvertisedPrice},

  props: {
    item: {
      type: Object,
      default: () => {},
    },

    columns: {
      type: Array,
      default: () => [],
    },

    disableToggles: {
      type: Boolean,
    },

    itemIndex: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      editedRetailLabelPrice: currencyToNumber(this.item.retailLabelPrice),
    };
  },

  computed: {
    ...mapState('backend', ['consumerFriendlyMode']),
    inputsDisabled() {
      return !this.item.retailLabelStatus;
    },
  },

  created() {
    if (this.item.retailLabelStatus) this.priceFormatToggler();

    if (this.item.retailLabelStatus === null) {
      this.$set(this.item, 'retailLabelStatus', false);
    }
    this.$validator.reset();
    this.dirtyCheck();
  },

  methods: {
    getClass(columns) {
      let column = columns.filter((column) => column.key == 'advertisedPrice');
      return column[0].isVisible;
    },

    dirtyCheck() {
      if (this.item.retailLabelStatus === this.item.initialRetailLabelStatus) {
        this.$set(this.item, 'dirty', false);
      } else {
        this.$set(this.item, 'dirty', true);
      }
    },

    toggleItem(payload) {
      this.item.retailLabelStatus = payload.value;
      this.priceFormatToggler();

      this.dirtyCheck();
    },

    // addresses BBD-5332 request for formatted price when not in edit mode
    priceFormatToggler() {
      if (this.item.retailLabelStatus) {
        this.$set(this.item, 'retailLabelPrice', this.editedRetailLabelPrice);
        this.$nextTick(() => this.$validator.validateAll());
      } else {
        this.editedRetailLabelPrice = this.item.retailLabelPrice;
        this.$set(this.item, 'retailLabelPrice', this.item.initialRetailLabelPrice);
      }
    },

    inputName(key) {
      return `${this.item.sku}-${key}`;
    },

    priceWarning(key) {
      if (key !== 'retailLabelPrice') return false;
      if (!this.item.retailLabelPrice) return false;
      if (!this.item.msrp) return false;

      // Warn the user if the price they've entered is less than the retail price
      if (typeof this.item.retailLabelStatus !== 'string') {
        return this.item.retailLabelPrice < currencyToNumber(this.item.msrp);
      }
      return currencyToNumber(this.item.retailLabelPrice) < currencyToNumber(this.item.msrp);
    },

    maxLength(columnKey) {
      if (columnKey === 'retailLabelDescription') return 60;

      return null;
    },

    setValidation(rules) {
      if (this.inputsDisabled) return false;

      return rules;
    },

    validatorValue(val) {
      return this.item.retailLabelStatus ? this.setValidation(val) : false;
    },
  },
};
</script>
